
import { memo } from 'react';

/**
 * Generic editable field component that supports:
 * - Text fields with inline editing
 * - Number fields with inline editing and validation
 * - Dropdown/select fields with click-to-cycle or full dropdown
 * - Proper keyboard navigation and accessibility
 */
const EditableField = memo(({
  field,
  value,
  type = 'text',
  options = null,
  onUpdate,
  editingField,
  setEditingField,
  className = '',
  placeholder = '',
  validation = {},
  formatValue = null,
  disabled = false,
  ...props
}) => {
  // Format display value if formatter provided
  const displayValue = formatValue ? formatValue(value) : value;

  // For dropdowns - click to cycle through options
  if (options && editingField !== field) {
    const handleClick = () => {
      if (disabled) return;
      const currentIndex = options.findIndex(o => o.value === value);
      const nextIndex = (currentIndex + 1) % options.length;
      onUpdate({ [field]: options[nextIndex].value });
    };

    return (
      <span
        onClick={handleClick}
        className={`px-1 -mx-0.5 rounded transition-colors font-medium ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-base-200 text-blue-600'} ${className}`}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        }}
        aria-label={`Click to change ${field}`}
        aria-disabled={disabled}
        {...props}
      >
        {options.find(o => o.value === value)?.label || displayValue}
      </span>
    );
  }

  // For numbers - inline input when editing
  if (type === 'number' && editingField === field) {
    const { min = 0, max = Infinity } = validation;

    return (
      <input
        autoFocus
        type="text"
        defaultValue={value}
        placeholder={placeholder}
        onFocus={(e) => e.target.select()}
        onBlur={(e) => {
          let val = parseInt(e.target.value);
          if (isNaN(val)) val = value;
          val = Math.max(min, Math.min(max, val));
          onUpdate({ [field]: val });
          setEditingField(null);
        }}
        onKeyDown={(e) => {
          // Allow only numbers and control keys
          if (!/[0-9]/.test(e.key) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter', 'Escape'].includes(e.key)) {
            e.preventDefault();
          }
          if (e.key === 'Enter') {
            let val = parseInt(e.target.value);
            if (isNaN(val)) val = value;
            val = Math.max(min, Math.min(max, val));
            onUpdate({ [field]: val });
            setEditingField(null);
          }
          if (e.key === 'Escape') {
            setEditingField(null);
          }
        }}
        style={{ width: `${Math.max(2, String(value).length * 0.8)}em` }}
        className={`inline-block text-center bg-base-200 rounded outline-none text-blue-600 font-medium px-1 -mx-0.5 ${className}`}
        {...props}
      />
    );
  }

  // For text - inline input when editing
  if (type === 'text' && editingField === field) {
    return (
      <input
        autoFocus
        type="text"
        defaultValue={value}
        placeholder={placeholder}
        onFocus={(e) => e.target.select()}
        onBlur={(e) => {
          let val = e.target.value;
          if (validation.trim) val = val.trim();
          if (!val && validation.required) val = value; // Revert if required and empty
          onUpdate({ [field]: val });
          setEditingField(null);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            let val = e.target.value;
            if (validation.trim) val = val.trim();
            if (!val && validation.required) val = value;
            onUpdate({ [field]: val });
            setEditingField(null);
          }
          if (e.key === 'Escape') {
            setEditingField(null);
          }
        }}
        style={{ width: `${Math.max(4, String(value).length * 0.8)}em` }}
        className={`inline-block bg-base-200 rounded outline-none text-blue-600 font-medium px-1 -mx-0.5 ${className}`}
        {...props}
      />
    );
  }

  // Default display mode - click to edit
  const handleClick = () => {
    if (disabled) return;
    if (options) {
      // For options, cycle through them
      const currentIndex = options.findIndex(o => o.value === value);
      const nextIndex = (currentIndex + 1) % options.length;
      onUpdate({ [field]: options[nextIndex].value });
    } else {
      // For text/number, enter edit mode
      setEditingField(field);
    }
  };

  return (
    <span
      onClick={handleClick}
      className={`px-1 -mx-0.5 rounded transition-colors font-medium ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-base-200 text-blue-600'} ${className}`}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      aria-label={`Click to edit ${field}`}
      aria-disabled={disabled}
      {...props}
    >
      {displayValue}
    </span>
  );
});

EditableField.displayName = 'EditableField';

export default EditableField;